import { PriceChangesChart, TsPoint } from '@toggle/design-system';
import { Entity, formatTicker } from '@toggle/toggle';
import { getDate, getMonth, getYear } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlagIcon } from '~/components/flag-icon/FlagIcon';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import * as S from './MarketReactionCharts.styles';

export interface MarketReactionChartProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  lines: { time: string; value: number }[][];
  entities?: Entity[];
}

export const MarketReactionChart = ({
  fromDateEST,
  toDateEST,
  lines,
  entities,
}: MarketReactionChartProps) => {
  const { t } = useTranslation(['earnings']);
  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.bullish.value,
    negative: colors.threshold.bearish.value,
  };

  if (!lines.length) {
    return null;
  }

  const multiColors = colors.getMulti(lines.length);
  const chartLines = lines.map((d, i) => {
    const data: TsPoint[] = d.map(item => ({
      date: toZonedTime(new Date(item.time), 'America/New_York'),
      value: item.value,
    }));
    return {
      data,
      label: '',
      color: lines.length > 1 ? multiColors[i].value : 'var(--icon-default)',
    };
  });

  const mergedValues = lines.flatMap(line => line.map(item => item.value));
  const range = Math.max(...mergedValues) - Math.min(...mergedValues);
  const yAxisFormatType = range > 4 ? 'percent' : 'percent1f';

  const getVerticalLines = () => {
    if (!fromDateEST || !toDateEST) {
      return [];
    }

    return [
      {
        x: fromDateEST,
      },
      {
        x: new Date(
          getYear(fromDateEST),
          getMonth(fromDateEST),
          getDate(fromDateEST),
          16,
          0,
          0,
          0
        ),
      },
      {
        x: new Date(
          getYear(toDateEST),
          getMonth(toDateEST),
          getDate(toDateEST),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: toDateEST,
      },
    ];
  };

  const isMultipleEntities = !!entities && entities.length > 1;
  return (
    <S.Container>
      {isMultipleEntities ? (
        <S.MultipleLegend>
          {entities.map((entity, index) => (
            <S.EtfLegendItem key={entity.tag}>
              <S.IconWrapper>
                <S.LegendItemVector $color={multiColors[index].value} />
                <FlagIcon
                  countryCode={entity.country?.long}
                  variant="circular"
                  customSize={16}
                />
              </S.IconWrapper>
              <S.EtfDetails>
                <S.EtfName>{entity.name_short}</S.EtfName>
                <S.EtfTag>{formatTicker(entity)}</S.EtfTag>
              </S.EtfDetails>
            </S.EtfLegendItem>
          ))}
        </S.MultipleLegend>
      ) : (
        <S.SingleLegend>
          <S.LegendItemVector $color="var(--border-default)" />
          <S.SingleLegendItemLabel>
            {t('earnings:earningsInsight.drawer.earningsRecap.marketOpenClose')}
          </S.SingleLegendItemLabel>
        </S.SingleLegend>
      )}
      {!!lines.length && (
        <PriceChangesChart
          lines={chartLines}
          fillColors={fillColors}
          yAxisFormatType={yAxisFormatType}
          thresholdData={
            chartLines.length === 1 ? chartLines[0].data : undefined
          }
          verticalLines={getVerticalLines()}
        />
      )}
    </S.Container>
  );
};
